/*
  OK FBI — Stream Platforms Connection Modal
  نافذة عامة وموحدة لربط منصات البث الرسمية:
    • TikTok (TikTok OAuth 2.0 PKCE)
    • Twitch (Twitch Helix OAuth 2.0)
    • Kick (Kick OAuth 2.1 PKCE)
  
  - تتيح للستريمر اختيار المنصة التي يريد ربطها
  - أخذ البيانات تلقائياً عبر OAuth دون إدخال يدوي
  - ربط أكثر من منصة لنفس الستريمر والتحكم المستقل بكل منصة
  - مخصصة لصاحب البث / الستريمر / المالك فقط
*/
import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { api, BASE_URL } from "@/lib/api";
import { toast } from "sonner";
import {
  X, Radio, Wifi, WifiOff, RefreshCw, ExternalLink,
  CheckCircle2, AlertCircle, Shield, Activity
} from "lucide-react";

export function StreamPlatformsModal({ isOpen, onClose, onRefresh }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [config, setConfig] = useState({
    twitch: { configured: false },
    kick: { configured: false },
    tiktok: { configured: false },
  });

  const [twitchConn, setTwitchConn] = useState(null);
  const [kickConn, setKickConn] = useState(null);
  const [tiktokConn, setTiktokConn] = useState(null);
  const containerRef = useRef(null);

  const loadStatuses = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const [cfgRes, twRes, kcRes, ttRes] = await Promise.allSettled([
        api.get("/stream/platforms"),
        api.get("/twitch/status"),
        api.get("/kick/status"),
        api.get("/tiktok/status"),
      ]);

      if (cfgRes.status === "fulfilled" && cfgRes.value?.data) {
        setConfig(cfgRes.value.data);
      }
      if (twRes.status === "fulfilled") {
        setTwitchConn(twRes.value?.data?.connection || null);
      }
      if (kcRes.status === "fulfilled") {
        setKickConn(kcRes.value?.data?.connection || null);
      }
      if (ttRes.status === "fulfilled") {
        setTiktokConn(ttRes.value?.data || null);
      }
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error("Failed to load stream platform statuses:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [onRefresh]);

  useEffect(() => {
    if (isOpen) {
      loadStatuses();
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
    }
  }, [isOpen, loadStatuses]);

  // Handle Disconnects
  const handleDisconnect = async (platform) => {
    try {
      if (platform === "tiktok") {
        await api.post("/tiktok/disconnect");
        toast.success("تم قطع الاتصال بـ TikTok");
      } else if (platform === "twitch") {
        await api.post("/twitch/disconnect");
        toast.success("تم قطع الاتصال بـ Twitch");
      } else if (platform === "kick") {
        await api.post("/kick/disconnect");
        toast.success("تم قطع الاتصال بـ Kick");
      }
      loadStatuses(true);
    } catch {
      toast.error(`فشل قطع الاتصال بـ ${platform}`);
    }
  };

  const handleTwitchSync = async () => {
    try {
      await api.post("/twitch/subscriptions/sync");
      toast.success("تمت مزامنة تنبيهات Twitch EventSub");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشلت مزامنة تنبيهات Twitch");
    }
  };

  const handleTiktokReconnect = async () => {
    try {
      await api.post("/tiktok/reconnect");
      toast.success("تمت إعادة تنشيط استقبال تعليقات TikTok");
      loadStatuses(true);
    } catch {
      toast.error("فشلت إعادة تنشيط TikTok");
    }
  };

  if (!isOpen) return null;

  const isTikTokConnected = !!tiktokConn?.connected;
  const isTwitchConnected = !!twitchConn?.connected;
  const isKickConnected = !!kickConn?.connected;
  const totalConnected = [isTikTokConnected, isTwitchConnected, isKickConnected].filter(Boolean).length;

  const apiBase = (BASE_URL || "").replace(/\/+$/, "");
  const token = (typeof window !== "undefined" ? localStorage.getItem("okfbi_token") : "") || "";
  const authQuery = token ? `?token=${encodeURIComponent(token)}` : "";

  const tiktokOAuthUrl = `${apiBase}/api/tiktok/start${authQuery}`;
  const twitchOAuthUrl = `${apiBase}/api/twitch/start${authQuery}`;
  const kickOAuthUrl = `${apiBase}/api/kick/start${authQuery}`;

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md"
      dir="rtl"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl bg-[#0D0D12] border border-neutral-800 rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.8)] overflow-hidden flex flex-col max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] min-h-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-neutral-800/80 bg-neutral-950/60 flex items-center justify-between shrink-0 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center text-yellow-400 shrink-0">
              <Radio size={20} className="animate-pulse" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base sm:text-lg font-black font-cairo text-white">
                  ربط وتفعيل منصات البث المباشر
                </h3>
                <span className="text-[11px] font-mono-ar px-2 py-0.5 rounded-full border border-neutral-700 bg-neutral-900 text-neutral-300 shrink-0">
                  {totalConnected}/3 متصل
                </span>
              </div>
              <p className="text-xs text-neutral-400 font-cairo mt-0.5">
                اربط حساباتك الرسمية عبر المصادقة المباشرة (OAuth) لاستقبال المتابعات والأحداث والتعليقات تلقائياً
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => loadStatuses()}
              disabled={refreshing}
              title="تحديث الحالة"
              className="p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-800/60 transition-colors cursor-pointer"
            >
              <RefreshCw size={15} className={refreshing ? "animate-spin text-yellow-400" : ""} />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-800/60 transition-colors cursor-pointer"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* 3-Column Platforms Grid */}
        <div ref={containerRef} className="p-4 sm:p-5 overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="py-12 text-center text-xs text-neutral-400 font-cairo">
              <RefreshCw size={24} className="animate-spin text-yellow-400 mx-auto mb-2" />
              جاري فحص حالة المنصات...
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* ─── 1. TIKTOK LIVE ─── */}
              <div
                className={`flex flex-col justify-between p-4 rounded-xl border transition-all ${
                  isTikTokConnected
                    ? "border-cyan-700/80 bg-gradient-to-b from-cyan-950/30 to-neutral-950/80 shadow-[0_0_20px_rgba(6,182,212,0.15)]"
                    : "border-neutral-800 bg-neutral-950/50 hover:border-cyan-800/50"
                }`}
              >
                <div>
                  {/* Card Header */}
                  <div className="flex items-center justify-between gap-2 mb-3 pb-3 border-b border-neutral-800/80">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-pink-500/20 border border-cyan-500/40 flex items-center justify-center font-black text-cyan-300 text-sm shrink-0 shadow-[0_0_12px_rgba(6,182,212,0.25)]">
                        TT
                      </div>
                      <div>
                        <h4 className="font-black text-sm text-white font-cairo flex items-center gap-1.5">
                          منصة TikTok LIVE
                        </h4>
                        <span className="text-[10px] text-cyan-400/80 font-mono">OAuth 2.0 PKCE</span>
                      </div>
                    </div>
                    <div>
                      {isTikTokConnected ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-cairo px-2 py-0.5 rounded-full border border-cyan-700 text-cyan-300 bg-cyan-950/60 font-bold">
                          <CheckCircle2 size={10} /> متصل ✅
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-cairo px-2 py-0.5 rounded-full border border-neutral-700 text-neutral-400 bg-neutral-900">
                          غير متصل ❌
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Card Info */}
                  <div className="text-xs font-cairo min-h-[56px] flex flex-col justify-center">
                    {isTikTokConnected ? (
                      <div className="space-y-1">
                        <div className="text-cyan-300 font-mono font-bold flex items-center gap-1 truncate">
                          <span>@{tiktokConn.display_name || tiktokConn.username || "حساب TikTok"}</span>
                        </div>
                        <div className="text-[11px] flex items-center gap-1.5">
                          <span className="text-neutral-400">حالة البث:</span>
                          <span className={tiktokConn.real_live ? "text-emerald-400 font-bold" : "text-neutral-500"}>
                            {tiktokConn.real_live ? "🟢 بث مباشر" : "⚪ غير مباشر"}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-neutral-400 leading-relaxed">
                        ربط قناة TikTok الرسمية واستقبال تعليقات وأصوات الجمهور تلقائياً.
                      </p>
                    )}
                  </div>
                </div>

                {/* Card Action */}
                <div className="pt-3 mt-3 border-t border-neutral-800/80">
                  {isTikTokConnected ? (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleTiktokReconnect}
                        className="flex-1 py-1.5 px-2 rounded-lg border border-neutral-700 hover:border-neutral-500 text-neutral-300 text-xs font-cairo transition-colors flex items-center justify-center gap-1 cursor-pointer"
                      >
                        <RefreshCw size={11} /> تنشيط
                      </button>
                      <button
                        onClick={() => handleDisconnect("tiktok")}
                        className="py-1.5 px-2.5 rounded-lg border border-red-900/60 hover:bg-red-950/40 text-red-400 text-xs font-cairo transition-colors flex items-center justify-center gap-1 cursor-pointer"
                      >
                        <WifiOff size={11} /> قطع
                      </button>
                    </div>
                  ) : (
                    <a
                      href={tiktokOAuthUrl}
                      className="w-full py-2 px-3 rounded-lg bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/50 text-cyan-300 text-xs font-bold font-cairo transition-all flex items-center justify-center gap-1.5 shadow-[0_0_15px_rgba(6,182,212,0.15)] text-center cursor-pointer"
                    >
                      <ExternalLink size={12} /> ربط الحساب (TikTok OAuth)
                    </a>
                  )}
                </div>
              </div>

              {/* ─── 2. TWITCH ─── */}
              <div
                className={`flex flex-col justify-between p-4 rounded-xl border transition-all ${
                  isTwitchConnected
                    ? "border-purple-700/80 bg-gradient-to-b from-purple-950/30 to-neutral-950/80 shadow-[0_0_20px_rgba(145,70,255,0.15)]"
                    : "border-neutral-800 bg-neutral-950/50 hover:border-purple-800/50"
                }`}
              >
                <div>
                  {/* Card Header */}
                  <div className="flex items-center justify-between gap-2 mb-3 pb-3 border-b border-neutral-800/80">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-xl bg-[#9146FF]/20 border border-[#9146FF]/40 flex items-center justify-center font-black text-[#C084FC] text-sm shrink-0 shadow-[0_0_12px_rgba(145,70,255,0.25)]">
                        T
                      </div>
                      <div>
                        <h4 className="font-black text-sm text-white font-cairo flex items-center gap-1.5">
                          منصة Twitch
                        </h4>
                        <span className="text-[10px] text-purple-400/80 font-mono">Helix API & EventSub</span>
                      </div>
                    </div>
                    <div>
                      {isTwitchConnected ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-cairo px-2 py-0.5 rounded-full border border-purple-700 text-purple-300 bg-purple-950/60 font-bold">
                          <CheckCircle2 size={10} /> متصل ✅
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-cairo px-2 py-0.5 rounded-full border border-neutral-700 text-neutral-400 bg-neutral-900">
                          غير متصل ❌
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Card Info */}
                  <div className="text-xs font-cairo min-h-[56px] flex flex-col justify-center">
                    {isTwitchConnected ? (
                      <div className="space-y-1">
                        <div className="text-purple-300 font-mono font-bold flex items-center gap-1 truncate">
                          <span>{twitchConn.display_name || twitchConn.login || "قناة Twitch"}</span>
                        </div>
                        <div className="text-[11px] flex items-center gap-1.5">
                          <span className="text-neutral-400">حالة البث:</span>
                          <span className={twitchConn.live_status === "online" ? "text-emerald-400 font-bold" : "text-neutral-500"}>
                            {twitchConn.live_status === "online" ? "🟢 بث مباشر" : "⚪ غير مباشر"}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-neutral-400 leading-relaxed">
                        ربط قناة Twitch واستقبال تفاعلات المتابعين و EventSub مباشرة.
                      </p>
                    )}
                  </div>
                </div>

                {/* Card Action */}
                <div className="pt-3 mt-3 border-t border-neutral-800/80">
                  {isTwitchConnected ? (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleTwitchSync}
                        className="flex-1 py-1.5 px-2 rounded-lg border border-purple-800/80 hover:bg-purple-950/30 text-purple-200 text-xs font-cairo transition-colors flex items-center justify-center gap-1 cursor-pointer"
                      >
                        <Activity size={11} /> مزامنة
                      </button>
                      <button
                        onClick={() => handleDisconnect("twitch")}
                        className="py-1.5 px-2.5 rounded-lg border border-red-900/60 hover:bg-red-950/40 text-red-400 text-xs font-cairo transition-colors flex items-center justify-center gap-1 cursor-pointer"
                      >
                        <WifiOff size={11} /> قطع
                      </button>
                    </div>
                  ) : (
                    <a
                      href={twitchOAuthUrl}
                      className="w-full py-2 px-3 rounded-lg bg-[#9146FF]/20 hover:bg-[#9146FF]/30 border border-[#9146FF]/60 text-[#D8B4FE] text-xs font-bold font-cairo transition-all flex items-center justify-center gap-1.5 shadow-[0_0_15px_rgba(145,70,255,0.15)] text-center cursor-pointer"
                    >
                      <ExternalLink size={12} /> ربط الحساب (Twitch OAuth)
                    </a>
                  )}
                </div>
              </div>

              {/* ─── 3. KICK ─── */}
              <div
                className={`flex flex-col justify-between p-4 rounded-xl border transition-all ${
                  isKickConnected
                    ? "border-emerald-700/80 bg-gradient-to-b from-emerald-950/30 to-neutral-950/80 shadow-[0_0_20px_rgba(83,252,24,0.15)]"
                    : "border-neutral-800 bg-neutral-950/50 hover:border-emerald-800/50"
                }`}
              >
                <div>
                  {/* Card Header */}
                  <div className="flex items-center justify-between gap-2 mb-3 pb-3 border-b border-neutral-800/80">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-xl bg-[#53FC18]/15 border border-[#53FC18]/40 flex items-center justify-center font-black text-[#53FC18] text-sm shrink-0 shadow-[0_0_12px_rgba(83,252,24,0.25)]">
                        K
                      </div>
                      <div>
                        <h4 className="font-black text-sm text-white font-cairo flex items-center gap-1.5">
                          منصة Kick
                        </h4>
                        <span className="text-[10px] text-emerald-400/80 font-mono">OAuth 2.1 PKCE</span>
                      </div>
                    </div>
                    <div>
                      {isKickConnected ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-cairo px-2 py-0.5 rounded-full border border-emerald-700 text-emerald-300 bg-emerald-950/60 font-bold">
                          <CheckCircle2 size={10} /> متصل ✅
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-cairo px-2 py-0.5 rounded-full border border-neutral-700 text-neutral-400 bg-neutral-900">
                          غير متصل ❌
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Card Info */}
                  <div className="text-xs font-cairo min-h-[56px] flex flex-col justify-center">
                    {isKickConnected ? (
                      <div className="space-y-1">
                        <div className="text-emerald-300 font-mono font-bold flex items-center gap-1 truncate">
                          <span>{kickConn.display_name || kickConn.login || "قناة Kick"}</span>
                        </div>
                        <div className="text-[11px] flex items-center gap-1.5">
                          <span className="text-neutral-400">حالة البث:</span>
                          <span className={kickConn.live_status === "online" ? "text-emerald-400 font-bold" : "text-neutral-500"}>
                            {kickConn.live_status === "online" ? "🟢 بث مباشر" : "⚪ غير مباشر"}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-neutral-400 leading-relaxed">
                        ربط قناة Kick الرسمية عبر تشفير PKCE واستقبال أحداث البث المباشر.
                      </p>
                    )}
                  </div>
                </div>

                {/* Card Action */}
                <div className="pt-3 mt-3 border-t border-neutral-800/80">
                  {isKickConnected ? (
                    <button
                      onClick={() => handleDisconnect("kick")}
                      className="w-full py-1.5 px-3 rounded-lg border border-red-900/60 hover:bg-red-950/40 text-red-400 text-xs font-cairo transition-colors flex items-center justify-center gap-1 cursor-pointer"
                    >
                      <WifiOff size={11} /> قطع الاتصال
                    </button>
                  ) : (
                    <a
                      href={kickOAuthUrl}
                      className="w-full py-2 px-3 rounded-lg bg-[#53FC18]/15 hover:bg-[#53FC18]/25 border border-[#53FC18]/50 text-[#53FC18] text-xs font-bold font-cairo transition-all flex items-center justify-center gap-1.5 shadow-[0_0_15px_rgba(83,252,24,0.15)] text-center cursor-pointer"
                    >
                      <ExternalLink size={12} /> ربط الحساب (Kick OAuth)
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 sm:p-4 border-t border-neutral-800 bg-neutral-950/80 flex items-center justify-between text-xs font-cairo text-neutral-400 shrink-0 gap-3">
          <div className="flex items-center gap-1.5 min-w-0">
            <Shield size={13} className="text-yellow-400 shrink-0" />
            <span className="truncate sm:whitespace-normal">يتم جلب الحساب وتوثيقه تلقائياً عبر المصادقة الرسمية لكل منصة بدون إدخال يدوي.</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-neutral-900 hover:bg-neutral-800 border border-neutral-700 text-white rounded-lg text-xs font-cairo transition-colors cursor-pointer shrink-0"
          >
            إغلاق
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default StreamPlatformsModal;
